import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CreditCard, CheckCircle2, AlertCircle, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPayments() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/connect/status");
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false, error: formatErr(e.response?.data?.detail) });
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (searchParams.get("connected") === "1") {
      toast.success("Stripe onboarding finished");
    }
  }, [searchParams]);

  const startConnect = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/payments/connect/start", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not start");
      setStarting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Stripe? Customers won't be able to pay online.")) return;
    setDisconnecting(true);
    try {
      await api.delete("/payments/connect");
      toast.success("Stripe disconnected");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setDisconnecting(false); }
  };

  if (user?.role !== "owner") {
    return (
      <div className="space-y-4 max-w-2xl">
        <Link to="/" className="inline-flex items-center text-sm text-slate-600 font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
        <h1 className="text-3xl font-bold">Payments</h1>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 text-slate-600">Only the company owner can connect a payment account.</div>
      </div>
    );
  }

  const connected = status?.connected && status?.charges_enabled;
  const onboardingIncomplete = status?.connected && !status?.charges_enabled;

  return (
    <div className="space-y-5 max-w-2xl">
      <Link to="/" className="inline-flex items-center text-sm text-slate-600 font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
      <h1 className="text-3xl font-bold">Settings · Payments</h1>

      <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <input type="text" name="fakeuser" autoComplete="username" className="hidden" tabIndex={-1} readOnly />
        <input type="password" name="fakepass" autoComplete="new-password" className="hidden" tabIndex={-1} readOnly />
        <div className="flex items-start gap-3">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${connected ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}`}>
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Stripe</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Connect your own Stripe account so customer payments (USD) land directly in your bank. Nothing flows through PlumberHQ.
            </p>

            {status === null ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Checking status…</div>
            ) : connected ? (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-50 text-green-800 ring-1 ring-inset ring-green-200 px-3 py-1.5 text-sm font-semibold" data-testid="stripe-connected">
                <CheckCircle2 className="w-4 h-4" /> Ready to accept payments
              </div>
            ) : onboardingIncomplete ? (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200 px-3 py-1.5 text-sm font-semibold" data-testid="stripe-incomplete">
                <AlertCircle className="w-4 h-4" /> Onboarding incomplete — finish it to accept payments
              </div>
            ) : (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200 px-3 py-1.5 text-sm font-semibold" data-testid="stripe-not-connected">
                <AlertCircle className="w-4 h-4" /> Not connected yet
              </div>
            )}

            {status?.error && (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="stripe-error">{status.error}</div>
            )}

            <div className="mt-4 flex gap-2 flex-wrap">
              <Button type="button" onClick={startConnect} disabled={starting} className="h-12 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="connect-stripe">
                {starting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening Stripe…</> : <><ExternalLink className="w-4 h-4 mr-1.5" /> {connected || onboardingIncomplete ? "Open Stripe" : "Connect Stripe"}</>}
              </Button>
              {(connected || onboardingIncomplete) && (
                <Button type="button" onClick={load} variant="outline" className="h-12 px-3 rounded-xl">Refresh status</Button>
              )}
              {(connected || onboardingIncomplete) && (
                <Button type="button" onClick={disconnect} disabled={disconnecting} variant="outline" className="h-12 px-3 rounded-xl text-red-600 hover:bg-red-50" data-testid="disconnect-stripe">Disconnect</Button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-500 space-y-1.5">
          <div><b>How it works:</b> we use Stripe Connect Express. You fill in your business details on Stripe's own onboarding page. Customer payments deposit into your bank on Stripe's normal payout schedule.</div>
          <div><b>Platform API key</b> (paste in <code className="bg-slate-100 px-1 rounded">backend/.env</code> as <code className="bg-slate-100 px-1 rounded">STRIPE_API_KEY</code>): your PlumberHQ Stripe account secret key from Stripe Dashboard → Developers → API keys (<code className="bg-slate-100 px-1 rounded">sk_test_…</code> or <code className="bg-slate-100 px-1 rounded">sk_live_…</code>). Then restart the backend. Each plumbing company connects their own account through this platform key.</div>
        </div>
      </form>
    </div>
  );
}
